import { useMemo, useState } from "react"
import { Mail, Phone, MapPin, Send, Clock, MessageCircle, Users, Globe, ChevronRight } from "lucide-react"
import { Button } from "../components/ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../components/Card"
import { useTranslation } from "../i18n"
import { motion } from "framer-motion"
import { usePublishedFAQs } from "../hooks/useFAQ"
import { useNavigate } from "react-router-dom"
import { useContactForm } from "../hooks/useContact"

export default function ContactPage() {
  const { tObj, language } = useTranslation()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  })

  // Hook pour envoyer le message de contact
  const { sendMessage, loading: isSubmitting, error: submitError } = useContactForm()

  // Récupérer les FAQs publiées (limitées à 3 pour l'aperçu)
  const { faqs: allFaqs, loading: faqsLoading, error: faqsError } = usePublishedFAQs({ language })
  const faqs = allFaqs?.slice(0, 3) || []

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      await sendMessage({
        fullName: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      })
      
      setFormData({ name: "", email: "", subject: "", message: "" })
    } catch (err) {
      // L'erreur est déjà gérée par le hook avec un toast
      console.error('Error sending contact message:', err)
    }
  }

  const contactInfo = [
    {
      icon: Mail,
      title: tObj.contact.email(),
      content: "info@youtheduc.com",
      description: tObj.contact.sendEmail()
    },
    {
      icon: Phone,
      title: tObj.contact.phone(),
      content: "(+225) 27 22 498 133",
      description: "(+225) 0596808080"
    },
    {
      icon: MapPin,
      title: tObj.contact.address(),
      content: "Côte d'Ivoire, Cocody",
      description: "Angré Djibi1 Centre"
    },
    {
      icon: Clock,
      title: tObj.contact.hours(),
      content: "8h00 - 17h00",
      description: tObj.contact.mondayToFriday()
    }
  ]

  // Fonction pour ouvrir WhatsApp avec message prérempli
  const handleWhatsAppChat = () => {
    const phoneNumber = "2250596808080" // Numéro sans espaces ni +
    const message = encodeURIComponent("Bonjour, j'aimerais avoir des informations sur AY Solutions Academy.")
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`
    window.open(whatsappUrl, '_blank')
  }

  const quickActions = [
    {
      icon: MessageCircle,
      title: tObj.contact.liveChat(),
      description: tObj.contact.liveChatDesc(),
      action: tObj.contact.startChat()
    },
    {
      icon: Users,
      title: tObj.contact.techSupport(),
      description: tObj.contact.techSupportDesc(),
      action: tObj.contact.contactSupport()
    },
    {
      icon: Globe,
      title: tObj.contact.faq(),
      description: tObj.contact.faqDesc(),
      action: tObj.contact.viewFAQ()
    }
  ]

  const heroLines = useMemo(() => {
    const subtitle = String(tObj.contact.subtitle?.() || "")
    const parts = subtitle.split(/(?<=[.!?])\s+/).filter(Boolean)
    if (parts.length <= 1) return [subtitle]
    if (parts.length === 2) return parts
    return [parts.slice(0, 2).join(" "), parts.slice(2).join(" ")]
  }, [tObj])

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
  }
  const rise = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  }

  const reveal = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-15% 0px" },
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header amélioré avec effets visuels - Style Courses */}
      <div className="relative pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-28 lg:pb-20 overflow-hidden">
        {/* Effets de fond animés */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#203a8a]/10 via-blue-50/50 to-purple-50/30"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-float delay-500"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent animate-gradient">
              {tObj.contact.title()}
            </h1>
            <div className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed space-y-2">
              {heroLines.map((line, idx) => (
                <p key={idx} className="text-gray-600">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info Grid - Amélioré */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 relative">
        {/* Effets de fond */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-200 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-200 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12 md:mb-16">
          {contactInfo.map((info, index) => (
            <motion.div key={index} {...reveal} transition={{ ...reveal.transition, delay: index * 0.05 }}>
            <Card className="text-center hover-lift hover-glow border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:bg-white group">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-[#203a8a] to-[#2563eb] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg">
                  <info.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-[#141835] mb-2 group-hover:text-[#203a8a] transition-colors">{info.title}</h3>
                <p className="text-[#141835] font-semibold mb-1">{info.content}</p>
                <p className={`text-sm ${info.title === tObj.contact.phone() ? 'text-[#141835] font-semibold' : 'text-gray-600'}`}>{info.description}</p>
              </CardContent>
            </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          {/* Contact Form - Amélioré */}
          <motion.div {...reveal}>
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-[#203a8a]/10 to-purple-50/50 rounded-t-xl">
                <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent">
                  {tObj.contact.sendMessage()}
                </CardTitle>
                <p className="text-sm sm:text-base text-gray-600">
                  {tObj.contact.sendMessageDesc()}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.contact.fullName()} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        minLength={2}
                        maxLength={100}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent transition-colors"
                        placeholder={tObj.contact.fullNamePlaceholder()}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.contact.email()} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent transition-colors"
                        placeholder={tObj.contact.emailPlaceholder()}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      {tObj.contact.subject()} *
                    </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        minLength={3}
                        maxLength={200}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent transition-colors"
                        placeholder={tObj.contact.subjectPlaceholder()}
                      />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      {tObj.contact.message()} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      minLength={10}
                      maxLength={5000}
                      rows={5}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent transition-colors resize-none"
                      placeholder={tObj.contact.messagePlaceholder()}
                    />
                    {submitError && (
                      <p className="mt-2 text-sm text-red-600">{submitError}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#203a8a] to-[#2563eb] hover:from-[#1a1f4a] hover:to-[#1e40af] text-white py-3 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        {tObj.contact.sending()}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Send className="w-5 h-5 mr-2" />
                        {tObj.contact.sendButton()}
                      </div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions & FAQ */}
          <motion.div className="space-y-8" {...reveal} transition={{ ...reveal.transition, delay: 0.08 }}>
            {/* Quick Actions - Amélioré */}
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 rounded-t-xl">
                <CardTitle className="bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent">
                  {tObj.contact.quickSolutions()}
                </CardTitle>
                <p className="text-gray-600">
                  {tObj.contact.quickSolutionsDesc()}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {quickActions.map((action, index) => (
                  <div 
                    key={index} 
                    className="group flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-[#203a8a] hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 cursor-pointer hover-lift"
                    onClick={() => {
                      if (action.title === tObj.contact.liveChat()) {
                        handleWhatsAppChat()
                      } else if (action.title === tObj.contact.faq()) {
                        navigate('/faq')
                      }
                    }}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-[#203a8a] to-[#2563eb] rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg">
                      <action.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-[#141835] group-hover:text-[#203a8a] transition-colors">{action.title}</h4>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="ml-4 border-2 hover:bg-[#203a8a] hover:text-white hover:border-[#203a8a] transition-all duration-300"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (action.title === tObj.contact.liveChat()) {
                          handleWhatsAppChat()
                        } else if (action.title === tObj.contact.faq()) {
                          navigate('/faq')
                        }
                      }}
                    >
                      {action.action}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* FAQ Preview - Amélioré */}
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-purple-50/50 to-pink-50/50 rounded-t-xl">
                <CardTitle className="bg-gradient-to-r from-[#141835] to-purple-600 bg-clip-text text-transparent">
                  {tObj.contact.frequentQuestions()}
                </CardTitle>
                <p className="text-gray-600">
                  {tObj.contact.faqSubtitle()}
                </p>
              </CardHeader>
              <CardContent>
                {faqsLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#141835] mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">Chargement des FAQs...</p>
                  </div>
                ) : faqsError ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Impossible de charger les FAQs pour le moment
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/faq')}
                    >
                      {tObj.contact.viewAllFAQ()}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : faqs.length > 0 ? (
                  <>
                    <div className="space-y-4">
                      {faqs.map((faq) => {
                        const question = faq.translation?.question || faq.question
                        const answer = faq.translation?.answer || faq.answer
                        return (
                          <div key={faq.id} className="border-l-4 border-[#141835] pl-4">
                            <h4 className="font-medium text-primary mb-1">
                              {question}
                            </h4>
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {answer}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate('/faq')}
                    >
                      {tObj.contact.viewAllFAQ()}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Aucune FAQ disponible pour le moment
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/faq')}
                    >
                      {tObj.contact.viewAllFAQ()}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Localités Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-16 relative overflow-hidden">
        {/* Effets de fond */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-200 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...reveal}>
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-4">
                Nos Bureaux
              </h2>
              <p className="text-lg text-gray-600">Retrouvez nos bureaux dans plusieurs villes de Côte d'Ivoire</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Bouaké */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#203a8a] to-[#2563eb] rounded-xl flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#141835] mb-3">Bouaké</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p className="font-medium text-gray-900">Quartier N'GATTAKRO</p>
                    <p>Derrière le stade de la Paix, non loin du collège Saint Viateur</p>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="font-medium text-gray-900">Contact :</p>
                      <p>M. Botty Donatien (Chef Bureau)</p>
                      <p className="text-[#203a8a] font-semibold">07 13 604 040</p>
                    </div>
                    <a 
                      href="https://maps.app.goo.gl/9gFY1dhEfqA7FAMK8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#203a8a] hover:text-[#2563eb] font-medium"
                    >
                      Voir sur Google Maps
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* San Pedro */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#203a8a] to-[#2563eb] rounded-xl flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#141835] mb-3">San Pedro</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p className="font-medium text-gray-900">Quartier Lac Francophonie</p>
                    <p>Carrefour Nabo, non loin de la base maritime à 50m de l'hôtel Ahiba</p>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="font-medium text-gray-900">Contact :</p>
                      <p>M. Mazahi Tchinsia (Chef Bureau)</p>
                      <p className="text-[#203a8a] font-semibold">07 13 606 001</p>
                    </div>
                    <a 
                      href="https://maps.app.goo.gl/6y8yPt3WpfZ7Mu5u5"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#203a8a] hover:text-[#2563eb] font-medium"
                    >
                      Voir sur Google Maps
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Daloa */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#203a8a] to-[#2563eb] rounded-xl flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#141835] mb-3">Daloa</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p className="font-medium text-gray-900">Quartier Gogoguhe</p>
                    <p>Derrière l'église des Assemblées de Dieu</p>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="font-medium text-gray-900">Contact :</p>
                      <p>Mme. Carelle Brika (Chef Bureau)</p>
                      <p className="text-[#203a8a] font-semibold">07 77 289 658</p>
                    </div>
                    <a 
                      href="https://maps.app.goo.gl/u778Ep5az4WsntYu8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#203a8a] hover:text-[#2563eb] font-medium"
                    >
                      Voir sur Google Maps
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Map Section - Amélioré */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-16 relative overflow-hidden">
        {/* Effets de fond */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-200 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...reveal}>
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-4">
              {tObj.contact.ourLocation()}
            </h2>
            <p className="text-lg text-gray-600">{tObj.contact.locationSubtitle()}</p>
          </div>
          <Card className="border-0 shadow-2xl hover:shadow-[0_0_50px_rgba(31,58,138,0.2)] transition-all duration-500">
            <CardContent className="p-0">
            <div className="w-full h-64 rounded-lg overflow-hidden">
  <iframe
    title="AY Solutions Academy Location"
    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3972.0561909591534!2d-3.982870825524653!3d5.408412835117225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfc1930022df6e79%3A0x363c78d64694d52d!2sAY%20Solutions%20-%20Centre%20d'insertion%20et%20d'incubation%20des%20jeunes!5e0!3m2!1sfr!2sci!4v1769436317317!5m2!1sfr!2sci"
    width="100%"
    height="100%"
    style={{ border: 0 }}
    allowFullScreen
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
  />
</div>

            </CardContent>
          </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
